import { ActionIcon, Badge, Box, Drawer, Flex, Group, Modal, Tabs, Text, Tooltip } from "@mantine/core";
import { IconBell, IconBellRinging, IconCreditCard, IconFileLike, IconFileText, IconFilter } from "@tabler/icons";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RepaymentApi } from "../../apis";
import { PageLayout } from "../../components/layout";
import { PayForm } from "../../components/returned/payForm";
import { ColumnType, Table } from "../../components/table";
import { IAuth } from "../../interfaces/IAuth";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../models/General";
import { currencyFormat } from "../../utils";
import { dateTimeFormat } from "../../utils/date";
import { Tag } from "./../../components/tags";
import { ReturnedDetails } from "./details";
import { PaymentMatching } from "../../components/repayment/payment-matching";

export const ReturnedList = () => {
  const breadcrumbs = useBreadcrumb();
  const [action, setAction] = useState<any[]>([]);

  const {
    currencies,
    productSuppFeeRules,
    repaymentStatus = [],
    programRepaymentRules = [],
    repaymentPaymentStatus,
    repaymentOverDueStatus = [],
    transactionTypes = [],
  } = useSelector((state: { general: IGeneral }) => state.general);
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);

  const tableRef = useRef<any>(null);

  const BellTypes = (record: any) => {
    return (
      <Group noWrap>
        {record?.remainingDays >= 2 && record?.remainingDays <= 3 && (
          <ActionIcon variant="light" color="green">
            <IconBell />
          </ActionIcon>
        )}

        {record?.remainingDays === 1 && (
          <ActionIcon variant="light" color="yellow">
            <IconBell />
          </ActionIcon>
        )}

        {record?.remainingDays === 0 && (
          <ActionIcon variant="light" color="orange">
            <IconBell />
          </ActionIcon>
        )}

        {record?.remainingDays < 0 && (
          <ActionIcon variant="light" color="red">
            <IconBellRinging />
          </ActionIcon>
        )}
      </Group>
    );
  };

  const columns = useHeader({
    onClick: async (key, record) => {
      switch (key) {
        case "detail":
          setAction(["detail", record]);
          break;

        case "payment_match":
          setAction(["payment_match", record]);
          break;

        case "pay":
          setAction(["pay", record]);
          break;
        default:
          break;
      }
    },
    Tag,
    BellTypes,
    repaymentStatus,
    currencies,
    productSuppFeeRules,
    programRepaymentRules,
    repaymentPaymentStatus,
    repaymentOverDueStatus,
    transactionTypes,
  });

  const reloadTable = async () => {
    await tableRef.current.reload();
  };

  return (
    <PageLayout
      title="Буцаан дуудсан"
      subTitle="Буцаан дуудсан эргэн төлөлтийн нэхэмжлэхийн жагсаалт"
      breadcrumb={breadcrumbs}
      extra={[
        <Group key={0}>
          <ActionIcon variant="light" color="golomt" size={36}>
            <IconFilter />
          </ActionIcon>
        </Group>,
      ]}>
      <Tabs defaultValue={"2"}>
        <Tabs.List>
          {/* <Tabs.Tab py={"md"} value="1">
            Зээлийн шугамаар
          </Tabs.Tab> */}
          <Tabs.Tab py={"md"} value="2" disabled={user?.currentBusiness?.type === "BUYER"}>
            Факторинг
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="1">
          <Table
            ref={tableRef}
            name="product.list.BUYER_LED.true"
            columns={columns}
            filters={{
              query: "",
              // productType: "BUYER_LED",
              isRecalled: true,
            }}
            minWidth={2400}
            loadData={(data) => RepaymentApi.list(data!)}
          />
        </Tabs.Panel>
        <Tabs.Panel value="2">
          <Table
            ref={tableRef}
            name="product.list.SUPPLIER_LED"
            columns={columns}
            filters={{
              query: "",
              // productType: "SUPPLIER_LED",
              isRecalled: true,
            }}
            minWidth={2400}
            loadData={(data) => RepaymentApi.list(data!)}
          />
        </Tabs.Panel>
      </Tabs>

      <Modal
        centered
        size={"60%"}
        title={
          <Text fw={600} fz="lg">
            Төлөлт тохирох
          </Text>
        }
        opened={action[0] === "payment_match"}
        onClose={() => {
          setAction([]);
        }}>
        <PaymentMatching action={action} setAction={setAction} reload={reloadTable} />
      </Modal>
      <Drawer
        position="right"
        size={"70rem"}
        title={false}
        withCloseButton={false}
        opened={action[0] === "detail"}
        onClose={() => {
          setAction([]);
        }}>
        <div style={{ overflow: "auto" }}>
          <Box h={"100vh"}>
            <ReturnedDetails action={action} setAction={setAction} />
          </Box>
        </div>
      </Drawer>

      <Modal
        centered
        size="1200px"
        withCloseButton={false}
        opened={action[0] === "pay"}
        padding={0}
        onClose={() => {
          setAction([]);
        }}>
        <PayForm
          action={action}
          setAction={setAction}
          reload={async () => {
            await tableRef.current?.reload();
          }}
        />
      </Modal>
    </PageLayout>
  );
};

const useHeader = ({
  onClick,
  programRepaymentRules,
  repaymentPaymentStatus,
  repaymentOverDueStatus,
  repaymentStatus,
  Tag,
  BellTypes,
  transactionTypes,
}: {
  onClick: (key: string, record: any) => void;
  currencies: IReference[];
  productSuppFeeRules: any;
  programRepaymentRules: any;
  repaymentPaymentStatus: IReference[];
  repaymentOverDueStatus: IReference[];
  repaymentStatus: IReference[];
  BellTypes: any;
  Tag: any;
  transactionTypes: IReference[];
}): ColumnType<any>[] => [
  {
    title: "#",
    render: (_r, index) => `${index + 1}`,
  },
  {
    title: "Үйлдэл",
    render: (record) => {
      return (
        <Flex gap={8} wrap="nowrap">
          {BellTypes(record)}
          {record?.overdueStatus !== "NORMAL" && ( //&& record?.overdueStatus !== "ONE_THREE"
            <Tooltip label="Төлөлт тохирох" position="top">
              <ActionIcon disabled={record?.paymentStatus === "PAID"} variant="light" onClick={() => onClick("payment_match", record)} color="">
                <IconFileLike />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Төлөх" position="top">
            <ActionIcon disabled={record?.paymentStatus === "PAID"} variant="light" onClick={() => onClick("pay", record)} color="">
              <IconCreditCard />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Дэлгэрэнгүй" position="top">
            <ActionIcon variant="light" onClick={() => onClick("detail", record)} color="">
              <IconFileText />
            </ActionIcon>
          </Tooltip>
        </Flex>
      );
    },
  },
  {
    title: "ЭТН Код",
    render: (record) => (
      <Text fw={500} color="golomt" w="max-content">
        # {record?.refCode}
      </Text>
    ),
  },
  {
    title: "ЭТН төлөх дүн",
    render: (record) => (
      <Flex justify="flex-end">
        <Text fw={500} color="gray.7" w="max-content">
          {record?.repaymentAmount ? currencyFormat(record?.repaymentAmount) : "-"}
        </Text>
      </Flex>
    ),
  },
  {
    title: "Эргэн төлөх огноо",
    render: (record) => (
      <Text fw={500} color="gray.7" w="max-content">
        {record?.repaymentDate ? dateTimeFormat(record?.repaymentDate) : "-"}
      </Text>
    ),
  },
  {
    title: "Төлсөн дүн",
    render: (record) => (
      <Flex justify="flex-end">
        <Text fw={500} color="gray.7" w="max-content">
          {record?.paidAmount ? currencyFormat(record?.paidAmount) : "-"}
        </Text>
      </Flex>
    ),
  },
  {
    title: "Төлбөрийн үлдэгдэл",
    render: (record) => (
      <Flex justify="flex-end">
        <Text fw={500} color="gray.7" w="max-content">
          {record?.amountToPay ? currencyFormat(record?.amountToPay) : "-"}
        </Text>
      </Flex>
    ),
  },
  {
    title: "Төлбөрийн төлөв",
    render: (record) => (
      <div>
        {Tag(
          repaymentPaymentStatus.find((item: IReference) => item.code === record?.paymentStatus)?.color || "indigo",
          repaymentPaymentStatus.find((item: IReference) => item.code === record?.paymentStatus)?.name || "",
        )}
      </div>
    ),
  },
  {
    title: "Хугацаа хэтрэлт",
    render: (record) => {
      if (record?.overdueStatus >= 0)
        return (
          <Text fw={500} color="gray.7" w="max-content">
            {Tag(
              repaymentOverDueStatus.find((item: IReference) => item.code === record?.overdueStatus)?.color || "indigo",
              repaymentOverDueStatus.find((item: IReference) => item.code === record?.overdueStatus)?.name || "",
            )}
          </Text>
        );
      else
        return (
          <Text fw={500} color="gray.7" w="max-content">
            {Tag(
              repaymentOverDueStatus.find((item: IReference) => item.code === record?.overdueStatus)?.color || "indigo",
              repaymentOverDueStatus.find((item: IReference) => item.code === record?.overdueStatus)?.name || "",
            )}
          </Text>
        );
    },
  },
  {
    title: "Нэхэмжлэх",
    render: (record) => (
      <Text color="golomt" fw={500} w="max-content">
        # {record?.invRefCode}
      </Text>
    ),
  },
  {
    title: "Хүсэлт Код",
    render: (record) => (
      <Text color="golomt" fw={500} w="max-content">
        # {record?.scfRequestRefCode}
      </Text>
    ),
  },
  {
    title: "Үүсгэсэн огноо",
    render: (record) => (
      <Text color="gray.7" fw={500} w={"max-content"}>
        {dayjs(record?.createdAt).format("YYYY-MM-DD HH:mm")}
      </Text>
    ),
  },
  {
    title: "Хүсэлтийн төлөв",
    render: (record) => (
      <Text fw={500} color="gray.7" w="max-content">
        {Tag(
          repaymentStatus?.find((item: IReference) => item?.code === record?.repaymentStatus)?.color || "",
          repaymentStatus?.find((item: IReference) => item?.code === record?.repaymentStatus)?.name || "-",
        )}
      </Text>
    ),
  },
  {
    title: "Нэхэмжлэгч тал",
    render: (record) => (
      <Text fw={500} color="gray.7" w="max-content">
        {record?.receiver || "-"}
      </Text>
    ),
  },
  {
    title: "Хүлээж буй хугацаа",
    render: (record) => {
      if (record?.remainingDays >= 0) {
        return (
          <Text fw={500} color="indigo" w={"max-content"}>
            {record?.remainingDays || 0} хоног
          </Text>
        );
      } else
        return (
          <Text fw={500} color="red" w={"max-content"}>
            {Math.abs(record?.remainingDays)} хоног хэтэрсэн байна.
          </Text>
        );
    },
  },
  {
    title: "Нэхэмжлэх төлөх огноо",
    render: (record) => (
      <Text fw={500} color="gray.7" w="max-content">
        {record?.invDueDate ? dateTimeFormat(record?.invDueDate) : "-"}
      </Text>
    ),
  },
  {
    title: "Буцаан дуудах",
    render: (record) => (
      <Text fw={500} w="max-content">
        {record?.recourseTerm ? (
          <Badge color="green" size="sm">
            Тийм
          </Badge>
        ) : (
          <Badge color="red" size="sm">
            Үгүй
          </Badge>
        )}
      </Text>
    ),
  },
  {
    title: "Зогсоох огноо",
    render: (record) => (
      <Text fw={500} color="gray.7" w="max-content">
        {record?.restrictDate ? dateTimeFormat(record?.restrictDate) : "-"}
      </Text>
    ),
  },
  {
    title: "Алданги (Хувь)",
    render: (record) => (
      <Text fw={500} color="gray.7" w={110}>
        {record?.penaltyPercent || 0} %
      </Text>
    ),
  },
  {
    title: "Эргэн төлөх дүрэм",
    render: (record) => (
      <Text fw={500} color="gray.7" w="max-content">
        {programRepaymentRules?.find((mk: any) => mk?.code === record?.repaymentRule)?.name || "-"}
      </Text>
    ),
  },
  {
    title: "Нийлүүлэгч бизнес",
    render: (record) =>
      record?.payerBusiness ? (
        <div>
          <Text fw={500} color="gray.7" w="max-content">
            {record?.payerBusiness?.profileName}
          </Text>
          <Text fw={500} color="golomt" component="a">
            # {record?.payerBusiness?.refCode}
          </Text>
        </div>
      ) : (
        "-"
      ),
  },
  {
    title: "Нийлүүлэгч партнер",
    render: (record) => (
      <div>
        <Text fw={500} color="gray.7" w="max-content">
          {record?.payerBusiness?.partner?.businessName}
        </Text>
        <Text fw={500} color="golomt" component="a">
          # {record?.payerBusiness?.partner?.refCode}
        </Text>
      </div>
    ),
  },
  {
    title: "Олголт код",
    render: (record) =>
      record?.disbursementRef ? (
        <Text fw={500} color="golomt" w="max-content">
          # {record?.disbursementRef || "-"}
        </Text>
      ) : (
        "-"
      ),
  },
  {
    title: "Төлөлт авах данс",
    render: (record) => (
      <Text fw={500} color="gray.7" w="max-content">
        {record?.receiverAcc?.number || "-"}
      </Text>
    ),
  },
  {
    title: "Дансны нэр",
    render: (record) => (
      <Text fw={500} color="gray.7" w="max-content">
        {record?.receiverAcc?.name || "-"}
      </Text>
    ),
  },
  {
    title: "Төлөлт авах банк",
    render: (record) => (
      <Text fw={500} color="gray.7" w="max-content">
        {record?.receiverAcc?.bankName || "-"}
      </Text>
    ),
  },
];

const useBreadcrumb = () => [
  {
    to: "/",
    label: "Үндсэн",
  },
  {
    to: "/",
    label: "Санхүүжилт",
  },
  {
    label: "Буцаан дуудсан",
  },
];
